import { getRouteApi } from '@tanstack/react-router'
import { endOfDay, isWithinInterval, startOfDay } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Table from '@/components/Table/Table'
import ToggleGroup, {
  type ToggleGroupOption
} from '@/components/ToggleGroup/ToggleGroup'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { usePagination } from '@/hooks/usePagination'
import useSemesters from '@/queries/useSemesters'
import useStudentCoursesOptions from '@/queries/useStudentCoursesOptions'
import useStudentsOptions from '@/queries/useStudentOptions'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'
import { getFilterValue } from '@/utils/get-filter-value'

import styles from './AbsencesView.module.scss'
import type { AbsencesFiltersKeys } from '../constants/absences-filters'
import useAbsencesColumns from '../hooks/useAbsencesColumns'
import useClassAbsencesList from '../queries/useClassAbsencesList'
import type { ClassAbsencePresence } from '../types'
import { getAbsenceStatusOptions } from '../utils/get-absences-status-options'

const routeApi = getRouteApi(
  '/_auth/students-and-classes/classes/$classId/absences'
)

const AbsencesView = () => {
  const { t } = useTranslation('classes')

  const { classId } = routeApi.useParams()
  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const pagination = usePagination(routeApi)

  const defaultParams = {
    presence: search.presence,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE
  }

  const [classAbsencePresence, setClassAbsencePresence] =
    useState<ClassAbsencePresence>(search.presence)

  const toggleOptions: ToggleGroupOption<ClassAbsencePresence>[] = [
    {
      label: t('button.unexcused'),
      value: 'unexcused'
    },
    {
      label: t('button.excused'),
      value: 'excused'
    }
  ]
  const { data, isLoading } = useClassAbsencesList({
    classId: [classId],
    ...search
  })

  const { data: students, isLoading: isStudentsLoading } = useStudentsOptions({
    classId: [classId]
  })

  const { data: semesters, isLoading: isSemesterLoading } = useSemesters()
  const { data: courses, isLoading: isCoursesLoading } =
    useStudentCoursesOptions({
      classId,
      semester: search.semester
    })

  const handleSemesterChange = (value: string) => {
    navigate({
      search: {
        semester: value,
        ...defaultParams
      }
    })
  }

  const handleTogglePresence = (value: ClassAbsencePresence) => {
    setClassAbsencePresence(value)
    navigate({
      search: prev => ({
        ...prev,
        status: undefined,
        presence: value,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      })
    })
  }

  const selectedSemester = semesters?.getSemester(search.semester)

  const isDateInsideSelectedSemester = (date: Date) => {
    if (!search.semester || !selectedSemester) return false
    return isWithinInterval(startOfDay(date), {
      start: startOfDay(selectedSemester?.startDate),
      end: endOfDay(selectedSemester?.endDate)
    })
  }

  const shouldDateBeDisabled = (date: Date) =>
    !isDateInsideSelectedSemester(date)

  const filters: Filter[] = [
    {
      label: t('label.semester'),
      variant: 'semester',
      size: 'wide',
      filterProps: {
        id: 'semester',
        loading: isSemesterLoading,
        options: semesters?.semestersFromCurrentYearOptions || [],
        semesterList: semesters?.list || [],
        value: search.semester,
        onChange: handleSemesterChange
      }
    },
    {
      label: t('label.student'),
      variant: 'multiselect',
      filterProps: {
        id: 'student',
        multiple: true,
        loading: isStudentsLoading,
        options: students || [],
        value: search.studentId,
        placeholder: t('label.student'),
        onChange: value => handleChangeFilter('studentId', value)
      }
    },
    {
      label: t('label.course'),
      variant: 'multiselect',
      filterProps: {
        id: 'course',
        multiple: true,
        loading: isCoursesLoading,
        options: courses?.options || [],
        value: search.courseId,
        placeholder: t('label.course'),
        onChange: value => handleChangeFilter('courseId', value)
      }
    },
    {
      variant: 'multiselect',
      label: t('label.status'),
      filterProps: {
        id: 'type-filter',
        options: getAbsenceStatusOptions(),
        multiple: true,
        value: search.status,
        placeholder: t('label.status'),
        onChange: value => handleChangeFilter('status', value)
      },
      hidden: classAbsencePresence === 'excused'
    },
    {
      label: t('label.date-range'),
      variant: 'date-range',
      size: 'wide',
      filterProps: {
        id: 'range',
        value: {
          from: search.dateAfter ? new Date(search.dateAfter) : undefined,
          to: search.dateBefore ? new Date(search.dateBefore) : undefined
        },
        disabledDate: shouldDateBeDisabled,
        onChange: value => {
          handleChangeFilter('dateAfter', value?.from)
          handleChangeFilter('dateBefore', value?.to)
        },
        defaultMonth: selectedSemester?.startDate
      }
    }
  ]

  const handleChangeFilter = (
    key: AbsencesFiltersKeys,
    value?: string[] | Date
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const isSomeFilterSelected = checkIfFilterSelected(filters, ['semester'])

  const handleClearAll = () => {
    navigate({
      search: {
        semester: search.semester,
        ...defaultParams
      }
    })
  }

  const { columns, ...manageColumns } = useAbsencesColumns({
    presence: search.presence,
    routeApi
  })

  return (
    <>
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        manageColumns={manageColumns}
      />

      <div className={styles.toggleWrapper}>
        <ToggleGroup
          value={classAbsencePresence}
          onChange={handleTogglePresence}
          options={toggleOptions}
        />
      </div>

      <Table
        id="absence-table"
        columns={columns}
        data={data?.list ?? []}
        count={data?.count ?? 0}
        isLoading={isLoading}
        {...pagination}
        caption={t('header.absences')}
      />
    </>
  )
}
export default AbsencesView
